import { useContext } from 'react';

import { ManualInputContext } from '../../context/context';
import './SalesChartContainer.scss';
import SalesInfoPopup from './SalesInfoPopup';
import { IFactMarketShareDataPreviousQuartersResponseModel } from '../../../../models/factMarketShareDatas/FactMarketShareDataPreviousQuarterResponseModel';
import { FactMarketShareDataSummaryResponseModel } from '../../../../models/factMarketShareDatas/FactMarketShareDataSummaryResponseModel';

export interface ISalesChartContainerProps {
	index: string;
	quarter: number;
	rowData: FactMarketShareDataSummaryResponseModel;
	tablePreviousQuartersData: IFactMarketShareDataPreviousQuartersResponseModel;
	product: string;
	productCategory: string;
}

const SalesChartContainer = (props: ISalesChartContainerProps) => {
	const localContext = useContext(ManualInputContext);

	const getBarData = (
		barNumber: number,
		tablePreviousQuartersData: IFactMarketShareDataPreviousQuartersResponseModel,
		rowData: FactMarketShareDataSummaryResponseModel
	) => {
		const previousQuarterData = tablePreviousQuartersData?.quarterData;

		const data = previousQuarterData[barNumber];

		const totalMarketSharePotential =
			data.summary.find((p) => p.id === rowData.id)
				?.totalMarketSharePotential ?? 0;
		const totalSales =
			data.summary.find((p) => p.id === rowData.id)?.totalSales ?? 0;
		const year = previousQuarterData[barNumber].year;
		const quarter = previousQuarterData[barNumber].quarter;

		return {
			totalMarketSharePotential,
			totalSales,
			year,
			quarter
		};
	};

	const {
		index,
		quarter,
		rowData,
		tablePreviousQuartersData,
		product,
		productCategory
	} = props;

	const bar1Data = getBarData(0, tablePreviousQuartersData, rowData);
	const bar2Data = getBarData(1, tablePreviousQuartersData, rowData);
	const bar3Data = getBarData(2, tablePreviousQuartersData, rowData);
	const bar4Data = getBarData(3, tablePreviousQuartersData, rowData);

	const maxTotalSales = Math.max(
		bar1Data.totalSales,
		bar2Data.totalSales,
		bar3Data.totalSales,
		bar4Data.totalSales
	);

	const maxMarketSharePotential = Math.max(
		bar1Data.totalMarketSharePotential,
		bar2Data.totalMarketSharePotential,
		bar3Data.totalMarketSharePotential,
		bar4Data.totalMarketSharePotential
	);

	function showPopup() {
		localContext?.showGraphPopupWithData({
			maxTotalSales,
			maxMarketSharePotential,
			bar1Data,
			bar2Data,
			bar3Data,
			bar4Data,
			product,
			productCategory
		});
	}

	return (
		<div className='sales-chart__container'>
			<div className='sales-chart__wrapper'>
				<img
					className='sales-chart__search-icon'
					src='/assets/icon-search.svg'
					alt='search icon'
					onClick={showPopup}
					id={index}
				/>
				<svg
					className='sales-chart'
					width='50'
					height='26'
					onClick={showPopup}
				>
					<g>
						<rect
							x='0'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar1Data.totalMarketSharePotential /
											maxMarketSharePotential) *
									  100
							}%`}
						></rect>
						<rect
							x='6'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar1Data.totalSales / maxTotalSales) *
									  100
							}%`}
						></rect>
						<rect
							x='12'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar2Data.totalMarketSharePotential /
											maxMarketSharePotential) *
									  100
							}%`}
						></rect>
						<rect
							x='18'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar2Data.totalSales / maxTotalSales) *
									  100
							}%`}
						></rect>
						<rect
							x='24'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar3Data.totalMarketSharePotential /
											maxMarketSharePotential) *
									  100
							}%`}
						></rect>
						<rect
							x='30'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar3Data.totalSales / maxTotalSales) *
									  100
							}%`}
						></rect>
						<rect
							x='36'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar4Data.totalMarketSharePotential /
											maxMarketSharePotential) *
									  100
							}%`}
						></rect>
						<rect
							x='42'
							width='4'
							y='0'
							rx='4'
							height={`${
								maxTotalSales === 0
									? 1
									: (bar4Data.totalSales / maxTotalSales) *
									  100
							}%`}
						></rect>
					</g>
				</svg>
			</div>

			<SalesInfoPopup
				index={index}
				quarter={quarter}
				maxTotalSales={maxTotalSales}
				maxMarketSharePotential={maxMarketSharePotential}
				bar1Data={bar1Data}
				bar2Data={bar2Data}
				bar3Data={bar3Data}
				bar4Data={bar4Data}
				product={product}
				productCategory={productCategory}
			/>
		</div>
	);
};

export default SalesChartContainer;
